package xim.poc.game.configuration.v0.tactics

import kotlinx.serialization.Serializable
import xim.poc.EquipmentLook
import xim.poc.ItemModelSlot
import xim.poc.ModelLook
import xim.poc.RaceGenderConfig
import xim.poc.game.CombatStat
import xim.poc.game.configuration.constants.*
import xim.poc.game.configuration.v0.TacticsUnit
import xim.poc.game.configuration.v0.tactics.StatRank.*
import xim.poc.game.configuration.v0.tactics.TacticsJobGroup.*
import xim.poc.game.configuration.v0.tactics.behaviors.TacticsDisjoinedBehavior.Companion.unbridledSpells
import xim.resource.DatId
import xim.resource.SpellElement
import xim.resource.SpellElement.*
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

@Serializable
value class TacticsJobId(val id: Int)

enum class TacticsJobGroup(val baseJob: TacticsJobId?) {
    Amorph(tacticsJob_Leech),
    Aquan(tacticsJob_Crab),
    Arcana(null),
    Beast(tacticsJob_Rabbit),
    Bird(tacticsJob_BatTrio),
    Demon(tacticsJob_Imp),
    Dragon(tacticsJob_Puk),
    Elemental(tacticsJob_Pixie),
    Lizard(tacticsJob_Lizard),
    Plantoid(tacticsJob_Mandy),
    Vermin(tacticsJob_Beetle),
    Unclassified(null),
}

enum class StatRank(val value: Int, val multiplier: Float, val weaponPower: Float, val offset: Int = value - 1, val iconIndex: Int = 455 + offset) {
    F(value = 1, multiplier = 1.0f, weaponPower = 1.0f),
    E(value = 2, multiplier = 1.15f, weaponPower = 1.0f),
    D(value = 3, multiplier = 1.33f, weaponPower = 1.0f),
    C(value = 4, multiplier = 1.52f, weaponPower = 1.1f),
    B(value = 5, multiplier = 1.75f, weaponPower = 1.2f),
    A(value = 6, multiplier = 2.0f, weaponPower = 1.3f),
    S(value = 7, multiplier = 2.3f, weaponPower = 1.4f),
    X(value = 8, multiplier = 2.66f, weaponPower = 1.5f),
    ;

    companion object {
        private val byValue = values().associateBy { it.value }

        fun fromValue(value: Int): StatRank? {
            return byValue[value]
        }
    }

    fun next(): StatRank {
        return offset(1)
    }

    fun offset(o: Int): StatRank {
        return StatRank.values().getOrNull(this.ordinal + o) ?: this
    }

}

data class CombatStatRanks(
    val maxHp: StatRank,
    val maxMp: StatRank = F,
    val str: StatRank,
    val dex: StatRank,
    val vit: StatRank,
    val agi: StatRank,
    val int: StatRank,
    val mnd: StatRank,
    val chr: StatRank = F,
) {
    val multipliers = mapOf(
        CombatStat.maxHp to maxHp.multiplier,
        CombatStat.maxMp to maxMp.multiplier,
        CombatStat.str to str.multiplier,
        CombatStat.dex to dex.multiplier,
        CombatStat.vit to vit.multiplier,
        CombatStat.agi to agi.multiplier,
        CombatStat.int to int.multiplier,
        CombatStat.mnd to mnd.multiplier,
        CombatStat.chr to chr.multiplier,
    )

    fun offset(o: Int): CombatStatRanks {
        if (o == 0) { return this }

        return CombatStatRanks(
            maxHp.offset(o),
            maxMp.offset(o),
            str.offset(o),
            dex.offset(o),
            vit.offset(o),
            agi.offset(o),
            int.offset(o),
            mnd.offset(o),
            chr.offset(o),
        )
    }

}

sealed interface TacticsEvolution {
    fun check(unit: TacticsUnit): Boolean
}

class TacticsUnitEvolution(val unlockLevel: Int): TacticsEvolution {
    override fun check(unit: TacticsUnit): Boolean {
        return unit.highestLevel >= unlockLevel
    }
}

class TacticsJobEvolution(val unlockJobId: TacticsJobId, val unlockLevel: Int): TacticsEvolution {
    override fun check(unit: TacticsUnit): Boolean {
        return (unit.jobLevels[unlockJobId]?.level ?: 0) >= unlockLevel
    }
}

class TacticsJobConfig(
    val id: TacticsJobId,
    val group: TacticsJobGroup,
    val displayName: String,
    val look: ModelLook,
    val minimumRank: StatRank = D,
    val spawnRank: StatRank = minimumRank,
    val maximumSpawnRank: StatRank = spawnRank.next(),
    val stats: CombatStatRanks,
    val element: SpellElement,
    val movement: Int = 2,
    val skillsByLevel: Map<Int, List<SkillId>>,
    val evolveRequirements: List<TacticsEvolution> = emptyList(),
    val playable: Boolean = true,
    val itemWorldValid: Boolean = playable,
    val modelScale: Float = 1f,
    val defaultAppearanceState: Int = 0,
    val spawnAnimations: List<DatId>? = listOf(DatId.spop),
    val deathAnimation: DatId? = DatId.specialDeath,
    val deathFadeTime: Duration? = null,
) {
    fun learnsSkill(skillId: SkillId): Boolean {
        return skillsByLevel.values.any { l -> l.any { it == skillId } }
    }
}

object TacticsJobConfigs {

    private val units: Map<TacticsJobId, TacticsJobConfig>
    private val sortedUnits: List<TacticsJobConfig>
    private val bySpawnRank: Map<StatRank, List<TacticsJobConfig>>

    init {
        val u = ArrayList<TacticsJobConfig>()

        u += TacticsJobConfig(id = tacticsJob_Pixie, group = Elemental, displayName = "Pixie", look = ModelLook.npc(0x7EE), element = Light,
            stats = CombatStatRanks(maxHp = E, str = F, dex = E, vit = F, agi = D, int = D, mnd = C),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillZephyrArrow_1937),
                3 to listOf(mskillAutumnBreeze_1941),
                10 to listOf(mskillCyclonicTurmoil_1943),
                12 to listOf(spellRepose_98),
                15 to listOf(mskillSpringBreeze_1939),
                20 to listOf(mskillLetheArrows_1938, spellChoke_237),
                25 to listOf(mskillSummerBreeze_1940),
                30 to listOf(mskillWinterBreeze_1942),
                35 to listOf(mskillCyclonicTorrent_1944),
                60 to listOf(mskillCyclonicBlight_2438),
                80 to listOf(mskillNornArrows_2262),
                90 to listOf(mskillEldritchWind_2566),
            ), cures, elem(Light), elem(Wind), protects, shells),
        )

        u += TacticsJobConfig(id = tacticsJob_Heartwing, group = Elemental, displayName = "Heartwing", look = ModelLook.npc(0xA0F), element = Light,
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = F, dex = F, vit = E, agi = E, int = D, mnd = C),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillImpairingGlister_2729),
                5 to listOf(spellErase_143),
                10 to listOf(spellAnimusAugeo_308, mskillAncestralBanishment_2727),
                20 to listOf(mskillHeartfeltAura_2728),
                25 to listOf(spellAnimusMinuo_309),
                40 to listOf(mskillCripplingGleam_2730),
            ), cures, curagas, elem(Light), elemAoe(Light), protects, shells, dias),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Baelfyr, group = Elemental, displayName = "Baelfyr", look = ModelLook.npc(0x865), element = Light,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = F, dex = F, vit = E, agi = E, int = C, mnd = C),
            skillsByLevel = combine(mapOf(
                20 to listOf(spellBurn_235),
                25 to listOf(mskillSearingTempest_2479, mskillBlindingFulgor_2480)
            ), elem(Fire), elemAoe(Fire), elem(Light), elemAoe(Light), dias),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Crab, group = Aquan, displayName = "Crab", look = ModelLook.npc(0x164), element = Water,
            stats = CombatStatRanks(maxHp = D, str = E, dex = E, vit = C, agi = F, int = F, mnd = D),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillBigScissors_188, mskillBubbleShower_186),
                3 to listOf(spellCrusade_476),
                4 to listOf(mskillMetallicBody_192),
                10 to listOf(mskillBubbleCurtain_187),
                15 to listOf(mskillScissorGuard_189),
                25 to listOf(mskillMegaScissors_2257),
                40 to listOf(mskillVenomShower_2256),
            ), cures, protects, shells, elem(Water)),
        )

        u += TacticsJobConfig(id = tacticsJob_Poroggo, group = Aquan, displayName = "Poroggo", look = ModelLook.npc(0x8BE), element = Water,
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = F, dex = F, vit = E, agi = E, int = C, mnd = D),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillFrogChorus_2652),
                5 to listOf(mskillFrogCheer_1704, spellDispel_260),
                10 to listOf(mskillWaterBomb_1703),
                12 to listOf(spellDispelga_360, spellSleep_253),
                15 to listOf(mskillMagicHammer_1702),
                20 to listOf(spellDrown_240),
                25 to listOf(mskillProvidence_1705),
                30 to listOf(spellSleepII_259),
                40 to listOf(mskillUsurpingScepter_2633),
            ), elemAoe(Water), elem(Ice), elemAoe(Ice)),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_SeaMonk, group = Aquan, displayName = "Sea Monk", look = ModelLook.npc(0x160), element = Water,
            minimumRank = B, modelScale = 0.66f, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = E, int = E, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillTentacle_200, mskillCrossAttack_204),
                10 to listOf(mskillInkJet_202),
                15 to listOf(mskillHardMembrane_203),
                25 to listOf(mskillMaelstrom_206),
                40 to listOf(mskillWhirlwind_207),
            ), regens, elem(Water), elemAoe(Water)),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Mandy, group = Plantoid, displayName = "Mandragora", look = ModelLook.npc(0x12C), element = Earth,
            stats = CombatStatRanks(maxHp = C, str = E, dex = D, vit = D, agi = F, int = F, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillHeadButt_44, mskillPhotosynthesis_48),
                3 to listOf(mskillLeafDagger_49),
                10 to listOf(mskillWildOats_46),
                15 to listOf(mskillScream_50),
                25 to listOf(mskillDreamFlower_45),
                30 to listOf(mskillDemonicFlower_2154),
                40 to listOf(mskillPhaeosynthesis_2323),
                50 to listOf(mskillPetalPirouette_1954),
                60 to listOf(mskillFatalScream_2131),
            ), elem(Earth)),
        )

        u += TacticsJobConfig(id = tacticsJob_Sabotender, group = Plantoid, displayName = "Sabotender", look = ModelLook.npc(0x174), element = Earth,
            minimumRank = C, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = E, int = F, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillNeedleshot_65, mskillPhotosynthesis_68),
                5 to listOf(mskill1000Needles_66, spellHaste_57),
                10 to listOf(spellRaptorMazurka_467),
                15 to listOf(mskill2000Needles_1369),
                20 to listOf(spellRefresh_109),
                25 to listOf(mskillChupaBlossom_2505),
                30 to listOf(spellHasteII_511),
                35 to listOf(spellChocoboMazurka_465),
                40 to listOf(mskill4000Needles_1370),
                60 to listOf(mskill10000Needles_864),
            )),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Leafkin, group = Plantoid, displayName = "Leafkin", look = ModelLook.npc(0x9E2), element = Earth,
            minimumRank = B, stats = CombatStatRanks(maxHp = C, str = E, dex = F, vit = D, agi = E, int = E, mnd = D),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillConsecration_2722, mskillSacredCaper_2723),
                5 to listOf(spellErase_143),
                10 to listOf(spellAnimusMinuo_309),
                15 to listOf(mskillPhototrophicWrath_2725),
                20 to listOf(spellAnimusAugeo_308, spellRasp_238),
                25 to listOf(mskillPhototrophicBlessing_2724),
                40 to listOf(mskillDeificGambol_2726),
            ), elem(Earth), elemAoe(Earth), cures),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Leech, group = Amorph, displayName = "Leech", look = ModelLook.npc(0x114), element = Dark,
            stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = E, agi = F, int = D, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillSuction_158, mskillSandBreath_160),
                5 to listOf(mskillDrainkiss_161),
                8 to listOf(mskillRegeneration_162),
                10 to listOf(mskillAcidMist_159),
                15 to listOf(mskillBrainDrain_167),
                25 to listOf(mskillAbsorbingKiss_70),
                40 to listOf(mskillCaliginosity_2681)
            ), elem(Water), poisons, regens),
        )

        u += TacticsJobConfig(id = tacticsJob_Hecteyes, group = Amorph, displayName = "Hecteyes", look = ModelLook.npc(0x180), element = Dark,
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = E, dex = F, vit = D, agi = F, int = C, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillHexEye_182, mskillPetroGaze_183),
                5 to listOf(spellDispel_260),
                12 to listOf(spellSleep_253),
                15 to listOf(mskillDeathRay_181),
                20 to listOf(spellDispelga_360, spellBurn_235),
                25 to listOf(mskillCatharsis_184),
                30 to listOf(spellSleepII_259),
                40 to listOf(spellImpact_503),
            ), elem(Fire), elemAoe(Fire), drains),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Slug, group = Amorph, displayName = "Slug", look = ModelLook.npc(0x7E5), element = Dark,
            minimumRank = B, stats = CombatStatRanks(maxHp = C, str = E, dex = E, vit = D, agi = F, int = D, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillFuscousOoze_1927, mskillPurulentOoze_1928),
                8 to listOf(spellSlow_56),
                10 to listOf(spellGravity_216),
                15 to listOf(mskillCorrosiveOoze_1929),
                18 to listOf(spellBind_258),
                20 to listOf(spellSlowga_357, spellDrown_240),
                25 to listOf(mskillMucilaginousOoze_1930, spellSlowII_79),
            ), elem(Dark)),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_BatTrio, group = Bird, displayName = "Bat Trio", look = ModelLook.npc(0x104), element = Wind,
            stats = CombatStatRanks(maxHp = E, str = D, dex = D, vit = F, agi = C, int = E, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillJetStream_139, mskillSlipstream_901),
                5 to listOf(mskillTurbulence_902),
                8 to listOf(mskillSonicBoom_137),
                10 to listOf(spellSheepfoeMambo_403),
                15 to listOf(mskillTrainFall_1086),
                25 to listOf(spellDragonfoeMambo_404),
                40 to listOf(mskillKnifeEdgeCircle_1085),
            ), elem(Wind), drains),
        )

        u += TacticsJobConfig(id = tacticsJob_Apkallu, group = Bird, displayName = "Apkallu", look = ModelLook.npc(0x6BB), element = Wind, defaultAppearanceState = 1,
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = D, dex = F, vit = E, agi = C, int = E, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillWingSlap_1458, mskillBeakLunge_1459),
                5 to listOf(mskillYawn_1457),
                8 to listOf(spellAdvancingMarch_419),
                10 to listOf(spellErase_143),
                15 to listOf(mskillFrigidShuffle_1460),
                20 to listOf(spellFrost_236),
                25 to listOf(mskillWingWhirl_1461),
                30 to listOf(spellVictoryMarch_420),
                40 to listOf(mskillWhiteout_2563),
            ), elem(Wind), elemAoe(Wind), elem(Ice)),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Tulfaire, group = Bird, displayName = "Tulfaire", look = ModelLook.npc(0xA23), modelScale = 0.8f, element = Wind,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = D, dex = E, vit = E, agi = C, int = E, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillPentapeck_2808, mskillSwoopingFrenzy_2809),
                6 to listOf(spellParalyze_58),
                15 to listOf(mskillMoltingPlumage_2807),
                20 to listOf(spellChoke_237),
                25 to listOf(mskillFromtheSkies_2810),
                30 to listOf(spellParalyzeII_80),
                40 to listOf(mskill_2811),
            ), elem(Wind), elem(Ice), elemAoe(Ice)),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Rabbit, group = Beast, displayName = "Rabbit", look = ModelLook.npc(0x10C), element = Lightning,
            stats = CombatStatRanks(maxHp = E, str = D, dex = C, vit = E, agi = D, int = F, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillFootKick_1, mskillDustCloud_2),
                3 to listOf(mskillWhirlClaws_3),
                4 to listOf(spellHaste_57),
                8 to listOf(mskillWildCarrot_67),
                15 to listOf(mskillSnowCloud_405),
                25 to listOf(mskillWildGinseng_1106, spellHastega_358),
                30 to listOf(spellHasteII_511),
            ), elem(Earth)),
        )

        u += TacticsJobConfig(id = tacticsJob_Coeurl, group = Beast, displayName = "Coeurl", look = ModelLook.npc(0x16F), modelScale = 0.7f, element = Lightning,
            minimumRank = C, stats = CombatStatRanks(maxHp = E, str = E, dex = C, vit = F, agi = D, int = D, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillPounce_226, mskillBlaster_396),
                5 to listOf(mskillPetrifactiveBreath_224),
                6 to listOf(spellParalyze_58),
                10 to listOf(mskillFrenziedRage_225),
                15 to listOf(spellDispel_260),
                20 to listOf(mskillChargedWhisker_227, spellShock_239),
                22 to listOf(spellParalyga_356),
                25 to listOf(mskillChaoticEye_397),
                30 to listOf(spellParalyzeII_80),
                40 to listOf(mskillPreternaturalGleam_2504),
                60 to listOf(mskillMortalBlast_2346),
            ), elem(Lightning), elemAoe(Lightning)),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Tiger, group = Beast, displayName = "Tiger", look = ModelLook.npc(0x134), element = Lightning,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = D, dex = C, vit = E, agi = D, int = E, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillRazorFang_15, mskillRoar_14),
                10 to listOf(mskillClawCyclone_17),
                15 to listOf(mskillDisorientingWaul_1951),
                20 to listOf(spellFrost_236),
                25 to listOf(mskillPredatoryGlare_1424),
                40 to listOf(mskillCrossthrash_1425),
            ), elem(Ice)),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Lizard, group = Lizard, displayName = "Lizard", look = ModelLook.npc(0x148), element = Fire,
            stats = CombatStatRanks(maxHp = D, str = C, dex = F, vit = E, agi = E, int = D, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillBlockhead_112, mskillFireball_111),
                3 to listOf(mskillSecretion_117),
                8 to listOf(mskillInfrasonics_116),
                10 to listOf(mskillSnowball_365),
                15 to listOf(mskillPlaguebreath_115),
                20 to listOf(spellBurn_235),
                25 to listOf(mskillBrainCrush_113),
                40 to listOf(mskillTailBlow_110),
            ), elem(Fire)),
        )

        u += TacticsJobConfig(id = tacticsJob_Eft, group = Lizard, displayName = "Eft", look = ModelLook.npc(0x545), element = Fire,
            minimumRank = C, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = F, agi = E, int = D, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillNimbleSnap_262, mskillToxicSpit_259),
                10 to listOf(mskillCyclotail_263),
                15 to listOf(mskillGeistWall_260),
                20 to listOf(spellDrown_240),
                25 to listOf(mskillNumbingNoise_261),
                40 to listOf(mskillTarichutoxin_2680),
            ), elem(Water), poisons),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Raptor, group = Lizard, displayName = "Raptor", look = ModelLook.npc(0x13C), element = Fire,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = D, dex = E, vit = E, agi = C, int = D, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillRipperFang_118, mskillChompRush_123),
                10 to listOf(mskillScytheTail_124, spellRaptorMazurka_467),
                15 to listOf(mskillFrostBreath_121),
                20 to listOf(mskillFoulBreath_120, spellFrost_236),
                25 to listOf(mskillThunderbolt_122, spellChocoboMazurka_465),
                40 to listOf(mskillWhirlingInferno_2787),
            ), elem(Ice)),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Bee, group = Vermin, displayName = "Bee", look = ModelLook.npc(0x110), element = Wind,
            maximumSpawnRank = E, stats = CombatStatRanks(maxHp = D, str = E, dex = F, vit = E, agi = C, int = F, mnd = D),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillSharpSting_78, mskillPollen_79),
            ), elem(Wind)),
            playable = false,
            itemWorldValid = true,
        )

        u += TacticsJobConfig(id = tacticsJob_Beetle, group = Vermin, displayName = "Beetle", look = ModelLook.npc(0x198), element = Earth,
            stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = F, int = F, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillPowerAttack_82, mskillSpoil_87),
                3 to listOf(spellCrusade_476),
                4 to listOf(spellStoneskin_54),
                10 to listOf(mskillRhinoGuard_85),
                15 to listOf(mskillRhinoAttack_84),
                25 to listOf(mskillHiFreqField_83),
                40 to listOf(mskillRhinowrecker_2567),
            ), protects, shells, elem(Earth), cures),
        )

        u += TacticsJobConfig(id = tacticsJob_Ladybug, group = Vermin, displayName = "Ladybug", look = ModelLook.npc(0x7E2), element = Earth,
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = C, dex = F, vit = E, agi = D, int = F, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillSuddenLunge_1922, mskillNoisomePowder_1923),
                8 to listOf(spellErase_143),
                10 to listOf(mskillNepentheanHum_1924),
                15 to listOf(mskillSpiralSpin_1925),
                20 to listOf(spellRefresh_109),
                25 to listOf(mskillSpiralBurst_1926),
                40 to listOf(mskillSearingEffulgence_2677),
            ), dias),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Chapuli, group = Vermin, displayName = "Chapuli", look = ModelLook.npc(0x9F1), element = Earth,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = C, dex = E, vit = E, agi = D, int = F, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillSensillaBlades_2690),
                5 to listOf(mskillSanguinarySlash_2692),
                8 to listOf(skillBoost_551),
                10 to listOf(mskillTegminaBuffet_2691),
                15 to listOf(spellChoke_237),
                20 to listOf(mskillNaturesMeditation_2689),
                25 to listOf(mskillShockiavona_2967),
                40 to listOf(mskillOrthopterror_2693),
            ), elem(Wind), elemAoe(Wind)),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Imp, group = Demon, displayName = "Imp", look = ModelLook.npc(0x6BE), element = Ice,
            stats = CombatStatRanks(maxHp = E, str = E, dex = D, vit = F, agi = D, int = C, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillAbrasiveTantara_1453, mskillDeafeningTantara_1454),
                5 to listOf(mskillFreneticRip_1455),
                8 to listOf(spellAdventurersDirge_469),
                10 to listOf(mskillBugleCall_1456),
                12 to listOf(spellDispel_260),
                15 to listOf(spellTractor_264),
                20 to listOf(mskillGratingTantara_1747),
                25 to listOf(mskillStiflingTantara_1748),
                40 to listOf(mskillDarkRecital_2632),
            ), elem(Ice), elemAoe(Ice), elem(Dark)),
        )

        u += TacticsJobConfig(id = tacticsJob_Ahriman, group = Demon, displayName = "Ahriman", look = ModelLook.npc(0x108), modelScale = 0.8f, element = Ice,
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = E, dex = E, vit = F, agi = D, int = C, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillBlindeye_292, mskillHypnosis_294),
                5 to listOf(spellBlind_254),
                10 to listOf(mskillEyesOnMe_293),
                15 to listOf(mskillMagicBarrier_299),
                18 to listOf(spellBind_258),
                20 to listOf(mskillMindBreak_295, spellChoke_237),
                22 to listOf(spellDispelga_360),
                25 to listOf(mskillBindingWave_296),
                28 to listOf(spellBlindII_276),
                40 to listOf(mskillDeathlyGlare_2512),
            ), elem(Wind), elemAoe(Wind)),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Gargouille, group = Demon, displayName = "Gargouille", look = ModelLook.npc(0x8B1), modelScale = 0.8f, element = Ice,
            minimumRank = B, stats = CombatStatRanks(maxHp = E, str = D, dex = E, vit = E, agi = D, int = C, mnd = F),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillBloodyClaw_2169, mskillTerrorEye_2168),
                10 to listOf(mskillDarkOrb_2165),
                12 to listOf(spellSleep_253),
                20 to listOf(mskillDarkMist_2166, spellBurn_235),
                25 to listOf(mskillTriumphantRoar_2167),
                30 to listOf(spellSleepII_259),
                40 to listOf(mskillShadowBurst_2170),
            ), elem(Fire), elemAoe(Fire), drains),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Bztavian, group = Vermin, displayName = "Bztavian", look = ModelLook.npc(0x9C4), element = Wind,
            spawnRank = S, minimumRank = A, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = C, int = D, mnd = E),
            skillsByLevel = mapOf(1 to listOf(mskillMandibularLashing_2746, mskillVespineHurricane_2747, mskillStingerVolley_2748, mskillDroningWhirlwind_2749, mskillIncisiveDenouement_2750, mskillIncisiveApotheosis_2751)),
            playable = false,
            modelScale = 0.5f,
        )

        u += TacticsJobConfig(id = tacticsJob_Puk, group = Dragon, displayName = "Puk", look = ModelLook.npc(0x6D0), element = Wind,
            stats = CombatStatRanks(maxHp = D, str = F, dex = F, vit = E, agi = C, int = D, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillCrosswind_1462, mskillObfuscate_1465),
                3 to listOf(mskillWhiteWind_1468),
                8 to listOf(spellFoeSirvente_468),
                10 to listOf(mskillZephyrMantle_1466),
                15 to listOf(mskillIllWind_1467),
                20 to listOf(spellChoke_237),
                25 to listOf(mskillWindShear_1464),
                40 to listOf(mskillSomnialDurance_2388),
            ), elem(Wind), paeons),
        )

        u += TacticsJobConfig(id = tacticsJob_Pet, group = Dragon, displayName = "Pet Wyvern", look = ModelLook.npc(0x018), element = Wind,
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = F, dex = F, vit = E, agi = C, int = D, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillFlameBreath_644, mskillGustBreath_646),
                5 to listOf(mskillSandBreath_647),
                10 to listOf(mskillHealingBreath_638, mskillHydroBreath_649),
                15 to listOf(mskillFrostBreath_645),
                20 to listOf(mskillHealingBreathII_639),
                25 to listOf(mskillLightningBreath_648),
                30 to listOf(mskillHealingBreathIII_640),
            ), elemAoe(Wind), minnes),
            evolveRequirements = listOf(TacticsUnitEvolution(10)),
        )

        u += TacticsJobConfig(id = tacticsJob_Dragon, group = Dragon, displayName = "Dragon", look = ModelLook.npc(0x1A4), modelScale = 0.5f, element = Dark,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = D, dex = E, vit = E, agi = F, int = C, mnd = E),
            skillsByLevel = combine(mapOf(
                1 to listOf(mskillHeavyStomp_390, mskillFlameBreath_386),
                5 to listOf(spellBattlefieldElegy_421),
                10 to listOf(mskillWindBreath_388),
                15 to listOf(mskillPetroEyes_392),
                20 to listOf(mskillPoisonBreath_387),
                25 to listOf(mskillBodySlam_389),
                30 to listOf(spellCarnageElegy_422),
                40 to listOf(mskillDiscordantNote_3507),
                70 to listOf(mskillChaosBlade_391),
            ), elem(Fire), elemAoe(Fire), requiems),
            evolveRequirements = listOf(TacticsUnitEvolution(25)),
        )

        u += TacticsJobConfig(id = tacticsJob_Spriggan, group = Unclassified, displayName = "Spriggan", look = ModelLook.npc(0xb42), element = Earth,
            minimumRank = B, stats = CombatStatRanks(maxHp = E, str = D, dex = D, vit = E, agi = C, int = F, mnd = F),
            skillsByLevel = mapOf(
                1 to listOf(mskillJitteringJig_2888, mskillRomp_2889, mskillFreneticFlurry_2890),
            ),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJob_MammetSword, group = Arcana, displayName = "Mammet", look = ModelLook.npcWithBase(0x3ED), element = Light,
            defaultAppearanceState = 1, spawnAnimations = null,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = D, dex = E, vit = E, agi = C, int = F, mnd = F),
            skillsByLevel = mapOf(
                1 to listOf(mskillTransmogrification_231, mskillVelociousBlade_91, mskillScissionThrust_163, mskillSonicBlade_166),
            ),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJob_MammetSpear, group = Arcana, displayName = "Mammet", look = ModelLook.npcWithBase(0x3ED), element = Dark,
            defaultAppearanceState = 2, spawnAnimations = null,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = C, dex = E, vit = D, agi = E, int = F, mnd = F),
            skillsByLevel = mapOf(
                1 to listOf(mskillTransmogrification_231, mskillPercussiveFoin_191, mskillMicroquake_185, mskillGravityWheel_201)
            ),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJob_MammetStaff, group = Arcana, displayName = "Mammet", look = ModelLook.npcWithBase(0x3ED), element = None,
            defaultAppearanceState = 3, spawnAnimations = null,
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = F, dex = E, vit = F, agi = E, int = C, mnd = D),
            skillsByLevel = mapOf(
                1 to listOf(mskillTransmogrification_231, mskillPsychomancy_208, mskillMindWall_215, spellFireII_145, spellBlizzardII_150, spellStoneII_160, spellAeroII_155, spellThunderII_165, spellWaterII_170)
            ),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJob_ShantottoII, group = Unclassified, displayName = "Shantotto", look = ModelLook.npc(0xC26), element = None,
            minimumRank = A, stats = CombatStatRanks(maxHp = D, str = E, dex = E, vit = E, agi = E, int = C, mnd = C),
            skillsByLevel = mapOf(
                1 to listOf(mskillFinalExam_3484, mskillDoctorsOrders_3485, mskillEmpiricalResearch_3486, mskillLessoninPain_3487)
            ),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJob_ShantottoClone, group = Unclassified, displayName = "Shantotto", element = None, look = shantottoCloneLook(),
            minimumRank = C, stats = CombatStatRanks(maxHp = D, str = E, dex = E, vit = E, agi = E, int = E, mnd = E),
            skillsByLevel = emptyMap(),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Blazenought, group = Arcana, displayName = "Blazenought", element = Fire, look = ModelLook.npc(0x959),
            deathAnimation = null,
            minimumRank = A, stats = CombatStatRanks(maxHp = C, str = C, dex = E, vit = D, agi = F, int = D, mnd = E),
            skillsByLevel = mapOf(1 to listOf(mskillTurbineCyclone_2363, mskillSeismicImpact_2364, mskillIncinerator_2365, mskillArmCannon_2366, mskillBallisticKick_2367)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Frostnought, group = Arcana, displayName = "Frostnought", element = Ice, look = ModelLook.npc(0x859),
            deathAnimation = null,
            minimumRank = A, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = F, int = C, mnd = E),
            skillsByLevel = mapOf(1 to listOf(mskillTurbineCyclone_2363, mskillSeismicImpact_2364, mskillIncinerator_2475, mskillArmCannon_2476, mskillBallisticKick_2477)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_DiscoidChariot, group = Arcana, displayName = "Chariot", element = Lightning, look = ModelLook.npc(0x720),
            minimumRank = A, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = F, int = C, mnd = D),
            skillsByLevel = mapOf(1 to listOf(mskillDiffusionRay_1798, mskillInertiaStream_1799, mskillDischarge_1800, mskillHomingMissile_1802, mskillDiscoid_1803)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Rampart, group = Arcana, displayName = "Rampart", element = Lightning, look = ModelLook.npc(0x71A),
            deathAnimation = null, deathFadeTime = 15.seconds,
            minimumRank = C, stats = CombatStatRanks(maxHp = C, str = E, dex = E, vit = D, agi = F, int = E, mnd = E), movement = 0,
            skillsByLevel = mapOf(1 to listOf(mskillRollerChain_1776, mskillChokeChain_1777, mskillReinforcements_1778, mskillBiomagnet_1779, mskillAstralGate_1780, mskillWarp_1781)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Gear, group = Arcana, displayName = "Gear", element = Lightning, look = ModelLook.npc(0x71B),
            minimumRank = B, stats = CombatStatRanks(maxHp = D, str = E, dex = E, vit = D, agi = C, int = E, mnd = E),
            skillsByLevel = mapOf(1 to listOf(mskillArtificialGravity_1782, mskillAntigravity_1783, mskillRailCannon_1784)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_DisjoinedOne, group = Unclassified, displayName = "???", element = None, look = ModelLook.npc(0x0),
            deathAnimation = null,
            minimumRank = A, stats = CombatStatRanks(maxHp = D, str = C, dex = E, vit = E, agi = E, int = C, mnd = E),
            skillsByLevel = mapOf(1 to listOf(skillRequiescat_226, skillUrielBlade_238, skillGlorySlash_239, skillUnbridledLearning_810, skillUnbridledWisdom_850, spellNatMeditation_700, spellSearingTempest_719, spellSpectralFloe_720, spellAnvilLightning_721, spellEntomb_722, spellBlindingFulgor_725, spellScouringSpate_726, spellSilentStorm_727, spellTenebralCrush_728) + unbridledSpells),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Selhteus, group = Unclassified, displayName = "???", element = Light, look = ModelLook.npc(0xC16),
            modelScale = 0.5f,
            minimumRank = A, stats = CombatStatRanks(maxHp = E, str = D, dex = F, vit = E, agi = F, int = D, mnd = C),
            skillsByLevel = mapOf(1 to listOf(mskillLuminousLance_3365, mskillRejuvenation_3366, mskillRevelation_3367)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Slash, group = Unclassified, displayName = "Slime", element = Water, look = ModelLook.npc(0xB41),
            minimumRank = A, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = F, agi = E, int = D, mnd = F),
            skillsByLevel = mapOf(1 to listOf(mskillFrizz_2871)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Sempurne, group = Unclassified, displayName = "???", element = Dark, look = ModelLook.npc(0x93F),
            modelScale = 0.5f, movement = 0,
            minimumRank = S, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = E, int = D, mnd = E),
            skillsByLevel = mapOf(1 to listOf(mskillCataclysmicVortex_2409, mskillCosmicBreath_2408, mskillCataclysmicVortex_2409, mskillGyreCharge_2410, mskillMightyGuard_2411, mskillAtomicRay_2412, mskillDarkMatter_2413, mskillProtostar_2414, mskillSupernova_2415)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Metus, group = Unclassified, displayName = "???", element = Dark, look = ModelLook.npc(0xB7B),
            spawnAnimations = null,
            modelScale = 0.5f,
            minimumRank = S, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = E, agi = F, int = D, mnd = E),
            skillsByLevel = mapOf(1 to listOf(mskillMalevolentBlessing_1244, mskillPestilentPenance_1245, mskillEmptySalvation_1246, mskillInfernalDeliverance_1247, mskillWheelofImpregnability_1248, mskillBastionofTwilight_1249, mskillWindsofOblivion_1250, mskillSealofQuiescence_1251, mskillCensure_3489, mskillOsmoticWave_3490)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Fenrir, group = Unclassified, displayName = "Fenrir", element = Dark, look = ModelLook.npc(0x318),
            spawnAnimations = null,
            modelScale = 0.75f,
            minimumRank = S, stats = CombatStatRanks(maxHp = C, str = D, dex = E, vit = D, agi = C, int = C, mnd = E),
            skillsByLevel = mapOf(1 to listOf(mskillMoonlitCharge_575, mskillCrescentFang_576, mskillLunarCry_577, mskillEclipticGrowl_578, mskillLunarRoar_579, mskillEclipseBite_580, mskillEclipticHowl_581, mskillHowlingMoon_582, mskillLunarBay_3295, mskillImpact_3296)),
            playable = false,
        )

        u += TacticsJobConfig(id = tacticsJobId_Carbuncle, group = Unclassified, displayName = "Carbuncle", element = Light, look = ModelLook.npc(0x317),
            spawnAnimations = null,
            minimumRank = S, stats = CombatStatRanks(maxHp = D, str = D, dex = D, vit = E, agi = C, int = E, mnd = C),
            skillsByLevel = mapOf(1 to listOf(mskillPoisonNails_651, mskillShiningRuby_652, mskillGlitteringRuby_653, mskillMeteorite_654, mskillHealingRubyII_655, mskillSearingLight_656, mskillHolyMist_3297)),
            playable = false,
        )

        units = u.associateBy { it.id }
        sortedUnits = u.sortedBy { it.id.id }
        bySpawnRank = u.groupBy { it.spawnRank }

        if (u.size != units.size) { throw IllegalStateException("Non-unique IDs!") }
    }

    fun random(): TacticsJobConfig {
        return units.values.random()
    }

    fun getAll(): List<TacticsJobConfig> {
        return sortedUnits
    }

    operator fun get(id: TacticsJobId): TacticsJobConfig {
        return units[id] ?: throw IllegalStateException("No such unit: $id")
    }

    operator fun get(statRank: StatRank): List<TacticsJobConfig> {
        return bySpawnRank[statRank] ?: emptyList()
    }

    fun getNullable(key: TacticsJobId): TacticsJobConfig? {
        return units[key]
    }

    private fun combine(base: Map<Int, List<SkillId>>, vararg standard: Map<Int, SkillId>): Map<Int, List<SkillId>> {
        val final = HashMap<Int, ArrayList<SkillId>>()

        for ((level, skills) in base) {
            final.getOrPut(level) { ArrayList() } += skills
        }

        for (s in standard) {
            for ((level, skill) in s) {
                final.getOrPut(level) { ArrayList() } += skill
            }
        }

        return final
    }

    fun elem(element: SpellElement, delay: Int = 0): Map<Int, SkillId> {
        val skills = when (element) {
            Fire -> spellFire_144.id .. spellFireV_148.id
            Ice -> spellBlizzard_149.id .. spellBlizzardV_153.id
            Wind -> spellAero_154.id .. spellAeroV_158.id
            Earth -> spellStone_159.id .. spellStoneV_163.id
            Lightning -> spellThunder_164.id .. spellThunderV_168.id
            Water -> spellWater_169.id .. spellWaterV_173.id
            Light -> spellBanish_28.id .. spellBanishV_32.id
            Dark -> spellBio_230.id .. spellBioV_234.id
            else -> return emptyMap()
        }.map { SpellSkillId(it) }

        return mapOf(
             1 + delay to skills[0],
            11 + delay to skills[1],
            21 + delay to skills[2],
            35 + delay to skills[3],
            61 + delay to skills[4],
        )
    }

    fun elemAoe(element: SpellElement, delay: Int = 0): Map<Int, SkillId> {
        val skills = when (element) {
            Fire -> spellFiraga_174.id .. spellFiragaV_178.id
            Ice -> spellBlizzaga_179.id .. spellBlizzagaV_183.id
            Wind -> spellAeroga_184.id .. spellAerogaV_188.id
            Earth -> spellStonega_189.id .. spellStonegaV_193.id
            Lightning -> spellThundaga_194.id .. spellThundagaV_198.id
            Water -> spellWaterga_199.id .. spellWatergaV_203.id
            Light -> spellBanishga_38.id .. spellBanishgaV_42.id
            else -> return emptyMap()
        }.map { SpellSkillId(it) }

        return mapOf(
             3 + delay to skills[0],
            14 + delay to skills[1],
            26 + delay to skills[2],
            49 + delay to skills[3],
            74 + delay to skills[4],
        )
    }

}

private val cures = mapOf(
    1 to spellCure_1,
    11 to spellCureII_2,
    21 to spellCureIII_3,
    41 to spellCureIV_4,
    61 to spellCureV_5,
)

private val curagas = mapOf(
    8 to spellCuraga_7,
    16 to spellCuragaII_8,
    31 to spellCuragaIII_9,
    51 to spellCuragaIV_10,
    71 to spellCuragaV_11,
)

private val regens = mapOf(
    1 to spellRegen_108,
    10 to spellRegenII_110,
    25 to spellRegenIII_111,
    40 to spellRegenIV_477,
    70 to spellRegenV_504,
)

private val poisons = mapOf(
    4 to spellPoisonga_225,
    16 to spellPoisongaII_226,
    29 to spellPoisongaIII_227,
    43 to spellPoisongaIV_228,
    68 to spellPoisongaV_229,
)

private val requiems = mapOf(
    3 to spellFoeRequiem_368,
    12 to spellFoeRequiemII_369,
    22 to spellFoeRequiemIII_370,
    33 to spellFoeRequiemIV_371,
    45 to spellFoeRequiemV_372,
)

private val minnes = mapOf(
    8 to spellKnightsMinne_389,
    16 to spellKnightsMinneII_390,
    26 to spellKnightsMinneIII_391,
    38 to spellKnightsMinneIV_392,
    50 to spellKnightsMinneV_393,
)

val protects = mapOf(
    1 to spellProtectra_125,
    23 to spellProtectraII_126,
    43 to spellProtectraIII_127,
    63 to spellProtectraIV_128,
    83 to spellProtectraV_129,
)

private val dias = mapOf(
    2 to spellDia_23,
    8 to spellDiaga_33,
    24 to spellDiaII_24,
    28 to spellDiagaII_34,
    44 to spellDiaIII_25,
    48 to spellDiagaIII_35,
    64 to spellDiaIV_26,
    68 to spellDiagaIV_36,
    84 to spellDiaV_27,
    88 to spellDiagaV_37,
)

val shells = mapOf(
    1 to spellShellra_130,
    23 to spellShellraII_131,
    43 to spellShellraIII_132,
    63 to spellShellraIV_133,
    83 to spellShellraV_134,
)

private val paeons = mapOf(
    1 to spellArmysPaeon_378,
    10 to spellArmysPaeonII_379,
    20 to spellArmysPaeonIII_380,
    30 to spellArmysPaeonIV_381,
    40 to spellArmysPaeonV_382,
    50 to spellArmysPaeonVI_383,
    60 to spellArmysPaeonVII_384,
    70 to spellArmysPaeonVIII_385,
)

private val drains = mapOf(
    3 to spellDrain_245,
    24 to spellDrainII_246,
    66 to spellDrainIII_880,
)

val tacticsJob_Pixie = TacticsJobId(1)
val tacticsJob_Heartwing = TacticsJobId(2)
val tacticsJob_Baelfyr = TacticsJobId(3)
val tacticsJob_Crab = TacticsJobId(10)
val tacticsJob_Poroggo = TacticsJobId(11)
val tacticsJob_SeaMonk = TacticsJobId(12)
val tacticsJob_Mandy = TacticsJobId(20)
val tacticsJob_Sabotender = TacticsJobId(21)
val tacticsJob_Leafkin = TacticsJobId(22)
val tacticsJob_Leech = TacticsJobId(30)
val tacticsJob_Hecteyes = TacticsJobId(31)
val tacticsJob_Slug = TacticsJobId(32)
val tacticsJob_BatTrio = TacticsJobId(40)
val tacticsJob_Apkallu = TacticsJobId(41)
val tacticsJob_Tulfaire = TacticsJobId(42)
val tacticsJob_Rabbit = TacticsJobId(50)
val tacticsJob_Coeurl = TacticsJobId(51)
val tacticsJob_Tiger = TacticsJobId(52)
val tacticsJob_Lizard = TacticsJobId(60)
val tacticsJob_Eft = TacticsJobId(61)
val tacticsJob_Raptor = TacticsJobId(62)
val tacticsJob_Bee = TacticsJobId(70)
val tacticsJob_Beetle = TacticsJobId(71)
val tacticsJob_Ladybug = TacticsJobId(72)
val tacticsJob_Chapuli = TacticsJobId(73)
val tacticsJob_Imp = TacticsJobId(100)
val tacticsJob_Ahriman = TacticsJobId(101)
val tacticsJob_Gargouille = TacticsJobId(102)
val tacticsJob_Bztavian = TacticsJobId(110)
val tacticsJob_Puk = TacticsJobId(120)
val tacticsJob_Pet = TacticsJobId(121)
val tacticsJob_Dragon = TacticsJobId(122)
val tacticsJob_Spriggan = TacticsJobId(130)

val tacticsJob_MammetSword = TacticsJobId(600)
val tacticsJob_MammetSpear = TacticsJobId(601)
val tacticsJob_MammetStaff = TacticsJobId(602)

val tacticsJob_ShantottoII = TacticsJobId(610)
val tacticsJob_ShantottoClone = TacticsJobId(611)

val tacticsJobId_Blazenought = TacticsJobId(620)
val tacticsJobId_Frostnought = TacticsJobId(621)

val tacticsJobId_DiscoidChariot = TacticsJobId(630)
val tacticsJobId_Rampart = TacticsJobId(631)
val tacticsJobId_Gear = TacticsJobId(632)

val tacticsJobId_DisjoinedOne = TacticsJobId(640)
val tacticsJobId_Sempurne = TacticsJobId(641)
val tacticsJobId_Selhteus = TacticsJobId(642)
val tacticsJobId_Slash = TacticsJobId(643)
val tacticsJobId_Metus = TacticsJobId(644)

val tacticsJobId_Fenrir = TacticsJobId(650)
val tacticsJobId_Carbuncle = TacticsJobId(651)

val jobResistanceTables: Map<SpellElement, Map<SpellElement, Int>> = mapOf(
    Fire to mapOf(Water to -50, Fire to 50, Ice to 50),
    Water to mapOf(Lightning to -50, Water to 50, Fire to 50),
    Lightning to mapOf(Earth to -50, Lightning to 50, Water to 50),
    Earth to mapOf(Wind to -50, Earth to 50, Lightning to 50),
    Wind to mapOf(Ice to -50, Wind to 50, Earth to 50),
    Ice to mapOf(Fire to -50, Ice to 50, Wind to 50),
    Light to mapOf(Dark to -50, Light to 50),
    Dark to mapOf(Light to -50, Dark to 50),
)

private fun shantottoCloneLook(): ModelLook {
    val shantottoCloneEquipment = EquipmentLook().also {
        it[ItemModelSlot.Main] = 324
        it[ItemModelSlot.Face] = 29
        it[ItemModelSlot.Head] = 19
        it[ItemModelSlot.Body] = 19
        it[ItemModelSlot.Legs] = 19
        it[ItemModelSlot.Hands] = 19
        it[ItemModelSlot.Feet] = 19
    }

    return ModelLook.pc(RaceGenderConfig.TaruM, equipmentLook = shantottoCloneEquipment)
}

val tacticsUnitNames = listOf(
    "Amedeo",
    "Astre",
    "Azure",
    "Baron",
    "Belorage",
    "Blutkralle",
    "Bo",
    "Bodo",
    "Bogen",
    "Buster",
    "Celeste",
    "Centonnerre",
    "Cerulean",
    "Chomper",
    "Coco",
    "Courageux",
    "Daisy",
    "Darug-Borug",
    "Delphyne",
    "Dino",
    "Donryu",
    "Duke",
    "Eisenzahn",
    "Ember",
    "Etoilazuree",
    "Etoile",
    "Felix",
    "Fido",
    "Firewing",
    "Flink",
    "Foudre",
    "Gaboh",
    "Galja-Mogalja",
    "George",
    "Gigima",
    "Ginger",
    "Gizmo",
    "Gogodavi",
    "Grisnuage",
    "Hien",
    "Himmelskralle",
    "Huffy",
    "Hyuh",
    "Jake",
    "Jessie",
    "Joseph",
    "Junker",
    "Jyubih",
    "Kagero",
    "Karav-Marav",
    "Khocha",
    "Knirps",
    "Koffla-Paffla",
    "Komet",
    "Lady",
    "Levian-Movian",
    "Lucy",
    "Lumiere",
    "Lumineux",
    "Lunaire",
    "Lune",
    "Maggie",
    "Majha",
    "Max",
    "Mikan",
    "Milo",
    "Missy",
    "Molly",
    "Muffin",
    "Nanaja",
    "Nonomi",
    "Nouvellune",
    "Nuage",
    "Oboro",
    "Orage",
    "Orageuse",
    "Oscar",
    "Paparal",
    "Pfeil",
    "Pouncer",
    "Qiqiru",
    "Quatrevents",
    "Raiden",
    "Rex",
    "Ringo",
    "Rocky",
    "Rover",
    "Rurumo",
    "Rusty",
    "Rygor",
    "Sasavi",
    "Solaire",
    "Soleil",
    "Soryu",
    "Soufflefeu",
    "Spike",
    "Stellaire",
    "Sylvester",
    "Tataba",
    "Tatang",
    "Teter",
    "Toby",
    "Tom",
    "Tonnerre",
    "Tora",
    "Toryu",
    "Totona",
    "Tranchevent",
    "Tupah",
    "Unryu",
    "Venteuse",
    "Vhiki",
    "Vhyun",
    "Waffenzahn",
    "Wawaro",
    "Wirbelwind",
    "Wuffi",
)